/**
 * Suay Blog Article — JavaScript
 * VTS-012: TOC Generation, Heading IDs, Anchor Links, Scroll Spy
 * VTS-013: Block Repositioning, IntersectionObserver, Mobile TOC Pill
 *
 * Single IIFE — no dependencies.
 * Scans .article-suay__content .rte for h2/h3 headings,
 * generates slug IDs, builds TOC nav, adds anchor links,
 * repositions blocks by data-position, highlights active TOC on scroll,
 * fades sidebar, lazy-loads Klaviyo, shows mobile TOC pill.
 */
;(function () {
  'use strict';

  /* ── Helpers ── */

  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function scrollBehavior() {
    return prefersReducedMotion ? 'auto' : 'smooth';
  }

  function slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/&/g, '-and-')
      .replace(/[\s\W]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function uniqueSlug(base, existing) {
    var slug = base;
    var counter = 1;
    while (existing[slug]) {
      slug = base + '-' + counter;
      counter++;
    }
    existing[slug] = true;
    return slug;
  }

  /* ── Block Repositioning (VTS-013) ── */

  function repositionBlocks(rte) {
    var article = document.querySelector('.article-suay');
    if (!article) return;

    var blocksZone = article.querySelector('.article-suay__blocks');
    if (!blocksZone) return;

    var blocks = blocksZone.querySelectorAll('[data-position]');
    if (!blocks.length) return;

    // Collect all h2 headings in the .rte (1-indexed for data-position)
    var h2s = rte.querySelectorAll('h2');

    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      var pos = parseInt(block.getAttribute('data-position'), 10) || 0;

      if (pos === 0) {
        // Position 0: stays in default zone below content — just reveal
        block.classList.add('is-positioned');
        continue;
      }

      // Target h2 index is pos - 1 (data-position 1 = after 1st h2)
      var targetIndex = pos - 1;

      if (targetIndex >= 0 && targetIndex < h2s.length) {
        var targetHeading = h2s[targetIndex];

        // Find the insertion point: after the heading and any content
        // until the next heading. We insert after the heading element itself.
        // This places the block between heading N and whatever follows it.
        var nextSibling = targetHeading.nextSibling;

        // Remove from blocks zone and insert into .rte after the heading
        if (nextSibling) {
          rte.insertBefore(block, nextSibling);
        } else {
          rte.appendChild(block);
        }
      }
      // else: target heading doesn't exist — block stays in default zone (graceful fallback)

      // Reveal block after positioning
      block.classList.add('is-positioned');
    }

    // Reveal any remaining blocks in the zone that weren't moved
    var remaining = blocksZone.querySelectorAll('[data-position]:not(.is-positioned)');
    for (var j = 0; j < remaining.length; j++) {
      remaining[j].classList.add('is-positioned');
    }
  }

  /* ── Klaviyo Lazy Embed (VTS-013) ── */

  var klaviyoLoaded = false;

  function loadKlaviyo() {
    if (klaviyoLoaded) return;
    klaviyoLoaded = true;

    // Find all Klaviyo embed containers and activate them
    var embeds = document.querySelectorAll('.article-suay__klaviyo-embed[data-klaviyo-form-id]');
    for (var i = 0; i < embeds.length; i++) {
      var formId = embeds[i].getAttribute('data-klaviyo-form-id');
      if (formId) {
        var div = document.createElement('div');
        div.className = 'klaviyo-form-' + formId;
        embeds[i].appendChild(div);
      }
    }

    // If Klaviyo's global JS isn't loaded yet, trigger it
    if (typeof window._klOnsite === 'undefined' && typeof window.klaviyo === 'undefined') {
      // Klaviyo is typically loaded by the theme's global snippet;
      // if present, the forms auto-render. If not, we leave it to
      // the site-wide Klaviyo setup.
    }
  }

  /* ── Main Init ── */

  function init() {
    var article = document.querySelector('.article-suay');
    if (!article) return;

    var rte = article.querySelector('.article-suay__content .rte');
    if (!rte) return;

    var headings = rte.querySelectorAll('h2, h3');

    /* ── Determine TOC min headings threshold ── */
    var tocAside = article.querySelector('.article-suay__toc');
    var tocMobile = article.querySelector('[data-toc-mobile]');
    var minHeadings = 3;

    if (tocAside && tocAside.getAttribute('data-toc-min-headings')) {
      minHeadings = parseInt(tocAside.getAttribute('data-toc-min-headings'), 10) || 3;
    } else if (tocMobile && tocMobile.getAttribute('data-toc-min-headings')) {
      minHeadings = parseInt(tocMobile.getAttribute('data-toc-min-headings'), 10) || 3;
    }

    /* ── Block Repositioning (VTS-013) — do before TOC so headings are final ── */
    repositionBlocks(rte);

    /* ── Generate heading IDs + anchor links (always, regardless of TOC) ── */
    var slugs = {};
    var tocEntries = [];

    // Re-query headings after block repositioning (DOM may have changed)
    headings = rte.querySelectorAll('h2, h3');
    if (!headings.length) return;

    for (var i = 0; i < headings.length; i++) {
      var heading = headings[i];
      var text = heading.textContent.trim();
      var level = heading.tagName === 'H2' ? 2 : 3;

      // Generate unique slug ID
      var slug = uniqueSlug(slugify(text), slugs);
      heading.id = slug;

      // Add anchor link
      var anchor = document.createElement('a');
      anchor.href = '#' + slug;
      anchor.className = 'article-suay__heading-anchor';
      anchor.setAttribute('aria-label', 'Link to ' + text);
      anchor.textContent = '#';
      heading.appendChild(anchor);

      tocEntries.push({
        id: slug,
        text: text,
        level: level,
        element: heading
      });
    }

    /* ── Build TOC if enough headings ── */
    var hasToc = tocEntries.length >= minHeadings;

    if (hasToc) {
      // Add .has-toc class to body-wrapper to activate two-column grid
      var bodyWrapper = article.querySelector('.article-suay__body-wrapper');
      if (bodyWrapper) {
        bodyWrapper.classList.add('has-toc');
      }

      /* Desktop TOC */
      var tocNav = article.querySelector('.article-suay__toc-nav');
      if (tocNav && tocAside) {
        var frag = document.createDocumentFragment();
        for (var j = 0; j < tocEntries.length; j++) {
          var entry = tocEntries[j];
          var link = document.createElement('a');
          link.href = '#' + entry.id;
          link.textContent = entry.text;
          link.setAttribute('data-level', entry.level);
          link.setAttribute('data-toc-index', j);
          frag.appendChild(link);
        }
        tocNav.appendChild(frag);
        tocAside.style.display = ''; // unhide (CSS default is display:none)
      }

      /* Mobile TOC */
      if (tocMobile) {
        var mobileNav = tocMobile.querySelector('.article-suay__toc-mobile-list');
        if (mobileNav) {
          var mobileFrag = document.createDocumentFragment();
          for (var k = 0; k < tocEntries.length; k++) {
            var mEntry = tocEntries[k];
            var mLink = document.createElement('a');
            mLink.href = '#' + mEntry.id;
            mLink.textContent = mEntry.text;
            mLink.setAttribute('data-level', mEntry.level);
            mobileFrag.appendChild(mLink);
          }
          mobileNav.appendChild(mobileFrag);
          tocMobile.classList.add('is-visible');
        }
      }

      /* ── Smooth scroll on TOC link click ── */
      function handleTocClick(e) {
        var target = e.target.closest('a[href^="#"]');
        if (!target) return;
        var id = target.getAttribute('href').slice(1);
        var el = document.getElementById(id);
        if (!el) return;
        e.preventDefault();
        var offset = 100; // account for sticky header
        var top = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: top, behavior: scrollBehavior() });
        // Update URL hash without jumping
        if (history.replaceState) {
          history.replaceState(null, '', '#' + id);
        }
      }

      if (tocNav) tocNav.addEventListener('click', handleTocClick);
      if (tocMobile) tocMobile.addEventListener('click', handleTocClick);
    }

    /* ── VTS-013: Single IntersectionObserver for TOC + Sidebar + Klaviyo ── */

    var tocLinks = hasToc && tocNav ? tocNav.querySelectorAll('a') : [];
    var activeIndex = -1;

    // Collect Klaviyo embed containers for lazy loading
    var klaviyoEmbeds = article.querySelectorAll('.article-suay__klaviyo-embed[data-klaviyo-form-id]');

    // Build array of observed entries: headings (for TOC), content bounds (for sidebar), Klaviyo
    // We use a single IntersectionObserver with different rootMargin for different purposes.
    // Strategy: observe all headings for TOC active state, observe the rte container for
    // sidebar fade, and observe Klaviyo embeds for lazy loading.

    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: no IntersectionObserver — reveal everything, load Klaviyo immediately
      if (klaviyoEmbeds.length) loadKlaviyo();
      return;
    }

    /* ── TOC Scroll Spy via IntersectionObserver ── */
    if (hasToc && tocLinks.length) {
      // Track which headings are currently in view
      var visibleHeadings = {};

      var tocObserver = new IntersectionObserver(function (entries) {
        for (var e = 0; e < entries.length; e++) {
          var ent = entries[e];
          var headingId = ent.target.id;
          if (ent.isIntersecting) {
            visibleHeadings[headingId] = true;
          } else {
            delete visibleHeadings[headingId];
          }
        }

        // Find the topmost visible heading (by DOM order)
        var newActiveIndex = -1;
        for (var h = 0; h < tocEntries.length; h++) {
          if (visibleHeadings[tocEntries[h].id]) {
            newActiveIndex = h;
            break;
          }
        }

        // If no heading is visible, find the last heading that's above viewport
        if (newActiveIndex === -1) {
          for (var h2 = tocEntries.length - 1; h2 >= 0; h2--) {
            var rect = tocEntries[h2].element.getBoundingClientRect();
            if (rect.top < 150) {
              newActiveIndex = h2;
              break;
            }
          }
        }

        if (newActiveIndex !== activeIndex) {
          activeIndex = newActiveIndex;
          for (var l = 0; l < tocLinks.length; l++) {
            if (l === activeIndex) {
              tocLinks[l].classList.add('is-active');
            } else {
              tocLinks[l].classList.remove('is-active');
            }
          }
        }
      }, {
        // Trigger when heading enters the top portion of viewport
        rootMargin: '-80px 0px -70% 0px',
        threshold: 0
      });

      for (var hi = 0; hi < tocEntries.length; hi++) {
        tocObserver.observe(tocEntries[hi].element);
      }
    }

    /* ── Sidebar Fade via IntersectionObserver ── */
    if (hasToc && tocAside) {
      var sidebarObserver = new IntersectionObserver(function (entries) {
        for (var e = 0; e < entries.length; e++) {
          // When the .rte content area is not visible, fade the sidebar
          if (entries[e].isIntersecting) {
            tocAside.removeAttribute('data-faded');
          } else {
            tocAside.setAttribute('data-faded', 'true');
          }
        }
      }, {
        rootMargin: '-100px 0px -100px 0px',
        threshold: 0
      });

      sidebarObserver.observe(rte);
    }

    /* ── Klaviyo Lazy Load via IntersectionObserver ── */
    if (klaviyoEmbeds.length) {
      var klaviyoObserver = new IntersectionObserver(function (entries) {
        for (var e = 0; e < entries.length; e++) {
          if (entries[e].isIntersecting) {
            loadKlaviyo();
            klaviyoObserver.disconnect();
            break;
          }
        }
      }, {
        // Load when within 200px of viewport
        rootMargin: '200px 0px',
        threshold: 0
      });

      for (var ki = 0; ki < klaviyoEmbeds.length; ki++) {
        klaviyoObserver.observe(klaviyoEmbeds[ki]);
      }
    }

    /* ── Mobile Floating TOC Pill (VTS-013) ── */
    if (hasToc && tocMobile) {
      // Create the pill button
      var pill = document.createElement('button');
      pill.className = 'article-suay__toc-pill';
      pill.setAttribute('aria-label', 'Jump to table of contents');
      pill.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 4h14M3 8h10M3 12h14M3 16h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      article.appendChild(pill);

      // Observe the inline mobile TOC — when it scrolls out of view downward, show the pill
      var pillObserver = new IntersectionObserver(function (entries) {
        for (var e = 0; e < entries.length; e++) {
          if (entries[e].isIntersecting) {
            pill.classList.remove('is-visible');
          } else {
            // Only show pill if user has scrolled past the TOC (it's above viewport)
            var tocRect = tocMobile.getBoundingClientRect();
            if (tocRect.bottom < 0) {
              pill.classList.add('is-visible');
            } else {
              pill.classList.remove('is-visible');
            }
          }
        }
      }, {
        threshold: 0
      });

      pillObserver.observe(tocMobile);

      // Tap pill → scroll back to inline TOC
      pill.addEventListener('click', function () {
        var offset = 100;
        var top = tocMobile.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: top, behavior: scrollBehavior() });
      });
    }
  }

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
